// smsSegments.js — how many SMS a piece of text actually costs.
//
// A deliberate mirror of node/helpers/smsSegments.js. The two live in separate
// repositories, so this is a copy rather than a shared import; the boundaries
// below come from GSM 03.38 and have not changed since 1998, which is what makes
// the duplication safe. The SERVER is authoritative — it recounts every message
// at /messages/send and meters against its own figure. This copy exists so the
// composer can show a cost before the user commits, not to decide anything.
//
// Carriers charge per 140 octets on the wire, which works out as:
//
//   GSM-7 (the 7-bit alphabet)   160 chars alone, 153 each once it splits
//   UCS-2 (anything else)         70 units alone,  67 each once it splits
//
// The drop to 153/67 is the concatenation header (UDH) eating 6 octets of every
// part, including the first — so a 161-character GSM-7 message is 2 segments,
// not 1 plus a scrap.
//
// This replaces a `livePreview.length > 160` check that was wrong twice over: a
// single "ø" flips the whole message to UCS-2 and its real ceiling to 70, and
// the extension characters below occupy two slots rather than one. A template
// that looked like one message could already be billing as three.

// GSM 03.38 basic set. One septet each. Written out literally rather than built
// from ranges — the accented and Greek entries are not contiguous with anything,
// and a table you can eyeball against the spec beats a clever construction.
const GSM7_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ' +
  ' !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§' +
  '¿abcdefghijklmnopqrstuvwxyzäöñüà';

// GSM 03.38 extension table. Each is sent as ESC + the character, costing *two*
// septets. Forgetting this is the classic off-by-a-few that puts a message one
// character over the line it was supposed to fit inside.
const GSM7_EXTENDED = '\f^{}\\[~]|€';

const BASIC = new Set(GSM7_BASIC);
const EXTENDED = new Set(GSM7_EXTENDED);

export const GSM7_SINGLE = 160;
export const GSM7_MULTI = 153;
export const UCS2_SINGLE = 70;
export const UCS2_MULTI = 67;

/**
 * Septet cost of `text` in GSM-7, or null if any character is unrepresentable
 * (in which case the whole message goes out as UCS-2).
 *
 * Iterates by code point, not UTF-16 unit, so an astral character — an emoji,
 * say — is tested once and correctly rejected, rather than split into two
 * surrogate halves that both miss the table for the wrong reason.
 */
function gsm7Length(text) {
  let septets = 0;
  for (const ch of text) {
    if (BASIC.has(ch)) septets += 1;
    else if (EXTENDED.has(ch)) septets += 2;
    else return null;
  }
  return septets;
}

/**
 * countSegments(text) → { encoding, length, segments, perSegment, remaining }
 *
 * Empty text is one segment: you cannot send nothing, and a caller handing us an
 * empty string has a bug we should not paper over by charging zero.
 */
export function countSegments(text) {
  const s = String(text ?? '');

  const septets = gsm7Length(s);
  const isGsm7 = septets !== null;

  // UCS-2 bills in UTF-16 code units, which is exactly what .length counts — so
  // an emoji outside the BMP is two units. Correct, not a bug: that really is
  // what goes on the wire.
  const length = isGsm7 ? septets : s.length;
  const single = isGsm7 ? GSM7_SINGLE : UCS2_SINGLE;
  const multi = isGsm7 ? GSM7_MULTI : UCS2_MULTI;

  let segments;
  let perSegment;
  if (length <= single) {
    segments = 1;
    perSegment = single;
  } else {
    segments = Math.ceil(length / multi);
    perSegment = multi;
  }

  return {
    encoding: isGsm7 ? 'GSM-7' : 'UCS-2',
    length,
    segments,
    perSegment,
    remaining: segments * perSegment - length,
  };
}

/** Just the billable count, for call sites that do not need the breakdown. */
export function segmentsFor(text) {
  return countSegments(text).segments;
}

/** Total segments across a batch — what the send will be metered at. */
export function totalSegments(messages) {
  return (messages || []).reduce((sum, m) => sum + segmentsFor(m && m.message), 0);
}
